import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import axios from 'axios';
import { 
  Users, 
  Plus, 
  Shield, 
  UserCog,
  User,
  Mail,
  Loader2,
  Crown,
  Edit,
  UserX,
  UserCheck,
  Trash2,
  Phone,
  Clock,
  Activity,
  ChevronRight,
  Calculator,
  X,
  RefreshCw,
  Search,
  MoreVertical,
  AlertCircle,
  CheckCircle2,
  Building2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_DESCRIPTIONS = {
  owner: {
    title: 'Owner',
    icon: Crown,
    color: 'amber',
    description: 'Full access to all features',
    permissions: ['Manage staff', 'Manage products', 'View reports', 'Configure settings', 'Full POS access']
  },
  manager: {
    title: 'Manager',
    icon: UserCog,
    color: 'blue',
    description: 'Manage operations and staff activities',
    permissions: ['Manage products', 'View reports', 'Manage transactions', 'Supervise cashiers']
  },
  cashier: {
    title: 'Cashier',
    icon: User,
    color: 'slate',
    description: 'Handle daily transactions',
    permissions: ['Access POS', 'Create transactions', 'Confirm payments', 'View recent transactions']
  },
  accountant: {
    title: 'Accountant',
    icon: Calculator,
    color: 'green',
    description: 'Financial oversight and reporting',
    permissions: ['View transactions', 'View financial reports', 'Export reports']
  }
};

export const Staff = () => {
  const { isDarkMode, branches } = useOutletContext() || {};
  const { staff: currentStaff } = useAuth();
  const [staffList, setStaffList] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('staff'); // staff, activity
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  
  // Form state
  const [staffForm, setStaffForm] = useState({
    email: '',
    name: '',
    phone: '',
    role: '',
    branch_id: ''
  });

  const fetchStaff = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/merchant/staff`, { withCredentials: true });
      setStaffList(response.data);
    } catch (error) {
      if (error.response?.status !== 403) {
        console.error('Error fetching staff:', error);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchActivityLogs = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/merchant/staff-activity?limit=50`, { withCredentials: true });
      setActivityLogs(response.data);
    } catch (error) {
      console.error('Error fetching activity logs:', error);
    }
  }, []);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  useEffect(() => {
    if (activeTab === 'activity') {
      fetchActivityLogs();
    }
  }, [activeTab, fetchActivityLogs]);

  const handleAddStaff = async (e) => {
    e.preventDefault();
    
    if (!staffForm.role) {
      toast.error('Please select a role');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...staffForm,
        branch_id: staffForm.branch_id || null
      };
      await axios.post(`${API_URL}/api/merchant/staff`, payload, { withCredentials: true });
      toast.success('Staff member added successfully');
      setShowAddModal(false);
      setStaffForm({ email: '', name: '', phone: '', role: '', branch_id: '' });
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add staff');
    } finally {
      setSaving(false);
    }
  };

  const handleEditStaff = async (e) => {
    e.preventDefault();
    if (!editingStaff) return;

    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/merchant/staff/${editingStaff.staff_id}`, {
        name: staffForm.name,
        phone: staffForm.phone,
        role: staffForm.role,
        branch_id: staffForm.branch_id || null
      }, { withCredentials: true });
      toast.success('Staff member updated');
      setShowEditModal(false);
      setEditingStaff(null);
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update staff');
    } finally {
      setSaving(false);
    }
  };

  const handleSuspend = async (staff) => {
    setActionLoading(staff.staff_id);
    try {
      await axios.post(`${API_URL}/api/merchant/staff/${staff.staff_id}/suspend`, {}, { withCredentials: true });
      toast.success(`${staff.name} has been suspended`);
      fetchStaff();
      fetchActivityLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to suspend staff');
    } finally {
      setActionLoading(null);
    }
  };

  const handleActivate = async (staff) => {
    setActionLoading(staff.staff_id);
    try {
      await axios.post(`${API_URL}/api/merchant/staff/${staff.staff_id}/activate`, {}, { withCredentials: true });
      toast.success(`${staff.name} has been activated`);
      fetchStaff();
      fetchActivityLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate staff');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemove = async (staff) => {
    if (!window.confirm(`Are you sure you want to remove ${staff.name}? This action cannot be undone.`)) {
      return;
    }
    
    setActionLoading(staff.staff_id);
    try {
      await axios.delete(`${API_URL}/api/merchant/staff/${staff.staff_id}`, { withCredentials: true });
      toast.success(`${staff.name} has been removed`);
      fetchStaff();
      fetchActivityLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove staff');
    } finally {
      setActionLoading(null);
    }
  };

  const openEditModal = (staff) => {
    setEditingStaff(staff);
    setStaffForm({
      name: staff.name,
      phone: staff.phone || '',
      role: staff.role,
      branch_id: staff.branch_id || ''
    });
    setShowEditModal(true);
  };

  const getRoleConfig = (role) => ROLE_DESCRIPTIONS[role] || ROLE_DESCRIPTIONS.cashier;

  const getRoleBadgeClasses = (role) => {
    const colors = {
      owner: isDarkMode ? 'bg-amber-900/50 text-amber-300 border-amber-700' : 'bg-amber-50 text-amber-700 border-amber-200',
      manager: isDarkMode ? 'bg-blue-900/50 text-blue-300 border-blue-700' : 'bg-blue-50 text-blue-700 border-blue-200',
      cashier: isDarkMode ? 'bg-slate-700 text-slate-300 border-slate-600' : 'bg-slate-50 text-slate-700 border-slate-200',
      accountant: isDarkMode ? 'bg-green-900/50 text-green-300 border-green-700' : 'bg-green-50 text-green-700 border-green-200'
    };
    return colors[role] || colors.cashier;
  };

  const getStatusBadge = (status) => {
    if (status === 'suspended') {
      return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
          isDarkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-700'
        }`}>
          <AlertCircle className="w-3 h-3" />
          Suspended
        </span>
      );
    }
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
        isDarkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-700'
      }`}>
        <CheckCircle2 className="w-3 h-3" />
        Active
      </span>
    );
  };

  const canManageStaff = currentStaff?.role === 'owner';
  
  const filteredStaff = staffList.filter(s => 
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionIcon = (action) => {
    const icons = {
      staff_added: <Plus className="w-4 h-4 text-green-500" />,
      staff_updated: <Edit className="w-4 h-4 text-blue-500" />,
      staff_suspended: <UserX className="w-4 h-4 text-red-500" />,
      staff_activated: <UserCheck className="w-4 h-4 text-green-500" />,
      staff_removed: <Trash2 className="w-4 h-4 text-red-500" />,
      transaction_created: <Plus className="w-4 h-4 text-teal-500" />,
      payment_confirmed: <CheckCircle2 className="w-4 h-4 text-green-500" />
    };
    return icons[action] || <Activity className="w-4 h-4 text-slate-400" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid="staff-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className={`text-2xl font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
            Staff Management
          </h1>
          <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            {staffList.length} team member{staffList.length !== 1 ? 's' : ''}
          </p>
        </div>
        {canManageStaff && (
          <Button 
            onClick={() => { setStaffForm({ email: '', name: '', phone: '', role: '' }); setShowAddModal(true); }}
            className="bg-teal-500 hover:bg-teal-600 text-white"
            data-testid="add-staff-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Staff
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className={`flex gap-1 p-1 rounded-lg mb-4 w-fit ${isDarkMode ? 'bg-slate-800' : 'bg-gray-100'}`}>
        <button
          onClick={() => setActiveTab('staff')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'staff'
              ? 'bg-white shadow text-slate-800'
              : isDarkMode ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-800'
          }`}
          data-testid="tab-staff"
        >
          <Users className="w-4 h-4 inline mr-2" />
          Staff List
        </button>
        {canManageStaff && (
          <button
            onClick={() => setActiveTab('activity')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'activity'
                ? 'bg-white shadow text-slate-800'
                : isDarkMode ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-800'
            }`}
            data-testid="tab-activity"
          >
            <Activity className="w-4 h-4 inline mr-2" />
            Activity Logs
          </button>
        )}
      </div>

      {/* Search */}
      {activeTab === 'staff' && (
        <div className={`relative mb-4 max-w-md`}>
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-400'}`} />
          <input
            type="text"
            placeholder="Search staff..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`w-full pl-10 pr-4 py-2 rounded-lg border ${
              isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200'
            }`}
          />
        </div>
      )}

      {/* Content */}
      <div className={`flex-1 overflow-auto rounded-xl ${isDarkMode ? 'bg-slate-800' : 'bg-white shadow-sm'}`}>
        {activeTab === 'staff' ? (
          /* Staff List */
          filteredStaff.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64">
              <Users className={`w-16 h-16 mb-4 ${isDarkMode ? 'text-slate-600' : 'text-gray-300'}`} />
              <p className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>No staff members found</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-700">
              {filteredStaff.map((member, idx) => {
                const roleConfig = getRoleConfig(member.role);
                const RoleIcon = roleConfig.icon;
                const isCurrentUser = member.staff_id === currentStaff?.staff_id;
                const isSuspended = member.status === 'suspended';
                
                return (
                  <div 
                    key={member.staff_id}
                    className={`p-4 flex items-center gap-4 transition-colors ${
                      isSuspended ? 'opacity-60' : ''
                    } ${isDarkMode ? 'hover:bg-slate-700/50' : 'hover:bg-gray-50'}`}
                    data-testid={`staff-${member.staff_id}`}
                  >
                    {/* Avatar */}
                    <div 
                      className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0 ${
                        isSuspended ? 'bg-gray-400' : 'bg-teal-500'
                      }`}
                    >
                      {member.name.charAt(0).toUpperCase()}
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className={`font-medium truncate ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                          {member.name}
                        </h3>
                        {isCurrentUser && (
                          <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-teal-900/50 text-teal-300' : 'bg-teal-100 text-teal-700'}`}>
                            You
                          </span>
                        )}
                        {getStatusBadge(member.status)}
                      </div>
                      <div className={`flex items-center gap-4 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {member.email}
                        </span>
                        {member.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {member.phone}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3 h-3" />
                          {member.branch_name || 'All Branches'}
                        </span>
                      </div>
                    </div>

                    {/* Role Badge */}
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium capitalize ${getRoleBadgeClasses(member.role)}`}>
                      <RoleIcon className="w-4 h-4" />
                      {member.role}
                    </span>

                    {/* Actions */}
                    {canManageStaff && !isCurrentUser && member.role !== 'owner' && (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => openEditModal(member)}
                          className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-slate-600 text-slate-400' : 'hover:bg-gray-100 text-slate-500'}`}
                          title="Edit"
                          data-testid={`edit-${member.staff_id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        
                        {isSuspended ? (
                          <button
                            onClick={() => handleActivate(member)}
                            disabled={actionLoading === member.staff_id}
                            className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-green-900/50 text-green-400' : 'hover:bg-green-100 text-green-600'}`}
                            title="Activate"
                            data-testid={`activate-${member.staff_id}`}
                          >
                            {actionLoading === member.staff_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <UserCheck className="w-4 h-4" />
                            )}
                          </button>
                        ) : (
                          <button
                            onClick={() => handleSuspend(member)}
                            disabled={actionLoading === member.staff_id}
                            className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-orange-900/50 text-orange-400' : 'hover:bg-orange-100 text-orange-600'}`}
                            title="Suspend"
                            data-testid={`suspend-${member.staff_id}`}
                          >
                            {actionLoading === member.staff_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <UserX className="w-4 h-4" />
                            )}
                          </button>
                        )}
                        
                        <button
                          onClick={() => handleRemove(member)}
                          disabled={actionLoading === member.staff_id}
                          className={`p-2 rounded-lg transition-colors hover:bg-red-100 text-red-500`}
                          title="Remove"
                          data-testid={`remove-${member.staff_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )
        ) : (
          /* Activity Logs */
          <div className="divide-y divide-slate-100 dark:divide-slate-700">
            {activityLogs.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64">
                <Activity className={`w-16 h-16 mb-4 ${isDarkMode ? 'text-slate-600' : 'text-gray-300'}`} />
                <p className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>No activity logs yet</p>
              </div>
            ) : (
              activityLogs.map((log, idx) => (
                <div 
                  key={log.log_id}
                  className={`p-4 flex items-start gap-3 ${isDarkMode ? 'hover:bg-slate-700/50' : 'hover:bg-gray-50'}`}
                >
                  <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                    {getActionIcon(log.action)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                      {log.description}
                    </p>
                    <div className={`flex items-center gap-3 text-sm mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {log.staff_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(log.timestamp)}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Role Legend */}
      <div className={`mt-4 p-4 rounded-xl ${isDarkMode ? 'bg-slate-800' : 'bg-gray-50'}`}>
        <h3 className={`text-sm font-medium mb-3 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-700'}`}>
          <Shield className="w-4 h-4" />
          Role Permissions
        </h3>
        <div className="grid grid-cols-4 gap-3">
          {Object.entries(ROLE_DESCRIPTIONS).map(([role, config]) => {
            const RoleIcon = config.icon;
            return (
              <div 
                key={role}
                className={`p-3 rounded-lg border transition-all ${
                  isDarkMode ? 'bg-slate-700/50 border-slate-600' : 'bg-white border-gray-200'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <RoleIcon className={`w-4 h-4 text-${config.color}-500`} />
                  <span className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-slate-700'}`}>
                    {config.title}
                  </span>
                </div>
                <ul className={`text-xs space-y-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {config.permissions.slice(0, 3).map((perm, i) => (
                    <li key={i} className="flex items-center gap-1">
                      <ChevronRight className="w-3 h-3" />
                      {perm}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>

      {/* Add Staff Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className={isDarkMode ? 'bg-slate-800 border-slate-700' : ''}>
          <DialogHeader>
            <DialogTitle className={isDarkMode ? 'text-white' : ''}>Add New Staff Member</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleAddStaff} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className={isDarkMode ? 'text-slate-300' : ''}>Email</Label>
              <Input
                type="email"
                value={staffForm.email}
                onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })}
                placeholder="staff@example.com"
                required
                className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}
                data-testid="staff-email-input"
              />
              <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                The user must already have a FlowLedger account
              </p>
            </div>

            <div className="space-y-2">
              <Label className={isDarkMode ? 'text-slate-300' : ''}>Display Name</Label>
              <Input
                type="text"
                value={staffForm.name}
                onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })}
                placeholder="John Doe"
                required
                className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}
                data-testid="staff-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className={isDarkMode ? 'text-slate-300' : ''}>Phone (Optional)</Label>
              <Input
                type="tel"
                value={staffForm.phone}
                onChange={(e) => setStaffForm({ ...staffForm, phone: e.target.value })}
                placeholder="+231 88 123 4567"
                className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}
                data-testid="staff-phone-input"
              />
            </div>

            <div className="space-y-2">
              <Label className={isDarkMode ? 'text-slate-300' : ''}>Role</Label>
              <Select 
                value={staffForm.role} 
                onValueChange={(value) => setStaffForm({ ...staffForm, role: value })}
              >
                <SelectTrigger className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="staff-role-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="cashier">Cashier</SelectItem>
                  <SelectItem value="accountant">Accountant</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className={isDarkMode ? 'text-slate-300' : ''}>Branch</Label>
              <Select 
                value={staffForm.branch_id || "all"} 
                onValueChange={(value) => setStaffForm({ ...staffForm, branch_id: value === "all" ? "" : value })}
              >
                <SelectTrigger className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="staff-branch-select">
                  <SelectValue placeholder="All Branches (for managers)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Branches</SelectItem>
                  {branches?.map((branch) => (
                    <SelectItem key={branch.branch_id} value={branch.branch_id}>
                      {branch.branch_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Cashiers and Accountants are typically assigned to a specific branch
              </p>
            </div>

            {staffForm.role && (
              <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-slate-700' : 'bg-slate-50'}`}>
                <p className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-slate-700'}`}>
                  {ROLE_DESCRIPTIONS[staffForm.role]?.title} Permissions:
                </p>
                <ul className={`text-xs space-y-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                  {ROLE_DESCRIPTIONS[staffForm.role]?.permissions.map((perm, i) => (
                    <li key={i} className="flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3 text-green-500" />
                      {perm}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAddModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-teal-500 hover:bg-teal-600"
                disabled={saving}
                data-testid="submit-staff-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Staff Member'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Staff Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className={isDarkMode ? 'bg-slate-800 border-slate-700' : ''}>
          <DialogHeader>
            <DialogTitle className={isDarkMode ? 'text-white' : ''}>Edit Staff Member</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleEditStaff} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className={isDarkMode ? 'text-slate-300' : ''}>Display Name</Label>
              <Input
                type="text"
                value={staffForm.name}
                onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })}
                required
                className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}
              />
            </div>

            <div className="space-y-2">
              <Label className={isDarkMode ? 'text-slate-300' : ''}>Phone</Label>
              <Input
                type="tel"
                value={staffForm.phone}
                onChange={(e) => setStaffForm({ ...staffForm, phone: e.target.value })}
                className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}
              />
            </div>

            <div className="space-y-2">
              <Label className={isDarkMode ? 'text-slate-300' : ''}>Role</Label>
              <Select 
                value={staffForm.role} 
                onValueChange={(value) => setStaffForm({ ...staffForm, role: value })}
              >
                <SelectTrigger className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="cashier">Cashier</SelectItem>
                  <SelectItem value="accountant">Accountant</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className={isDarkMode ? 'text-slate-300' : ''}>Branch</Label>
              <Select 
                value={staffForm.branch_id || "all"} 
                onValueChange={(value) => setStaffForm({ ...staffForm, branch_id: value === "all" ? "" : value })}
              >
                <SelectTrigger className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}>
                  <SelectValue placeholder="All Branches" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Branches</SelectItem>
                  {branches?.map((branch) => (
                    <SelectItem key={branch.branch_id} value={branch.branch_id}>
                      {branch.branch_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEditModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-teal-500 hover:bg-teal-600"
                disabled={saving}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Staff;
